/* eslint-disable no-console */
import http from '../lib/api';
import { SharePermission, KnowledgeBaseIconType, iconTypeToMetaData } from '../types/knowledgeBase';

export interface KnowledgeBase {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  is_public: boolean;
  is_default: boolean;
  owner_id: number;
  owner?: {
    id: number;
    email: string;
    full_name: string;
  };
  embedding_model_id?: string | null;
  embedding_model?: any; // Could be typed more specifically if we have the model type
  chunk_size: number;
  chunk_overlap: number;
  document_count: number; // Updated from files_count to match API
  total_tokens: number;
  meta_data?: any; // Could be typed more specifically
  created_at: string;
  updated_at: string;
  shares?: KnowledgeBaseShare[];
  share_count?: number; // Number of users the KB is shared with (excludes owner)
}

export interface KnowledgeBaseShare {
  id: string;
  knowledge_base_id: string;
  shared_with_user_id: number;
  permission: SharePermission;
  created_at: string;
}

// File type enum
export enum KnowledgeBaseFileType {
  PDF = 'pdf',
  DOC = 'doc',
  DOCX = 'docx',
  TXT = 'txt',
  MD = 'md',
  XLSX = 'xlsx',
  XLS = 'xls',
  CSV = 'csv',
  JSON = 'json',
  XML = 'xml',
}

// API response type for knowledge base files
export interface KnowledgeBaseFileApiResponse {
  id: string;
  original_name: string;
  file_type: string;
  size_bytes: number;
  processing_status: 'pending' | 'processing' | 'completed' | 'error';
  indexed: boolean;
  added_at: string;
  processed_at: string | null;
  // Article metadata fields
  title?: string;
  authors?: string[];
  journal?: string;
  year?: number;
  abstract?: string;
  keywords?: string[];
}

// Frontend KnowledgeBaseFile type (matches the interface in KnowledgeBaseFilesPage)
export interface KnowledgeBaseFile {
  id: string;
  file_id?: string; // 文件ID，用于轮询处理状态
  name: string;
  type: 'file' | 'folder';
  author?: string;
  upload_date: string;
  status: 'uploaded' | 'processing' | 'indexed' | 'error';
  size?: number;
  description?: string;
  file_type?: KnowledgeBaseFileType;
  page_count?: number;
  chunk_count?: number;
  // Article metadata fields
  title?: string;
  authors?: string[];
  journal?: string;
  year?: number;
  abstract?: string;
  keywords?: string[];
}

// API paginated response type
export interface KnowledgeBaseFilesPaginatedApiResponse {
  items: KnowledgeBaseFileApiResponse[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

// Frontend paginated response type
export interface PaginatedKnowledgeBaseFilesResponse {
  items: KnowledgeBaseFile[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

/**
 * Mapper for converting API responses to KnowledgeBaseFile models
 */
export class KnowledgeBaseFileMapper {
  /**
   * Maps API response file type to frontend file type
   */
  private mapApiFileTypeToFrontendType(apiFileType: string): KnowledgeBaseFileType | undefined {
    const lowerType = apiFileType.toLowerCase();

    const typeMap: Record<string, KnowledgeBaseFileType> = {
      pdf: KnowledgeBaseFileType.PDF,
      doc: KnowledgeBaseFileType.DOC,
      docx: KnowledgeBaseFileType.DOCX,
      txt: KnowledgeBaseFileType.TXT,
      md: KnowledgeBaseFileType.MD,
      markdown: KnowledgeBaseFileType.MD,
      xlsx: KnowledgeBaseFileType.XLSX,
      xls: KnowledgeBaseFileType.XLS,
      csv: KnowledgeBaseFileType.CSV,
      json: KnowledgeBaseFileType.JSON,
      xml: KnowledgeBaseFileType.XML,
    };

    return typeMap[lowerType];
  }

  /**
   * Infers file type from file name extension
   */
  private inferFileTypeFromFileName(fileName: string): KnowledgeBaseFileType | undefined {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (!extension) return undefined;

    const typeMap: Record<string, KnowledgeBaseFileType> = {
      pdf: KnowledgeBaseFileType.PDF,
      doc: KnowledgeBaseFileType.DOC,
      docx: KnowledgeBaseFileType.DOCX,
      txt: KnowledgeBaseFileType.TXT,
      md: KnowledgeBaseFileType.MD,
      markdown: KnowledgeBaseFileType.MD,
      xlsx: KnowledgeBaseFileType.XLSX,
      xls: KnowledgeBaseFileType.XLS,
      csv: KnowledgeBaseFileType.CSV,
      json: KnowledgeBaseFileType.JSON,
      xml: KnowledgeBaseFileType.XML,
    };

    return typeMap[extension];
  }

  /**
   * Maps API processing status to frontend status
   *
   * 状态映射规则：
   * - error -> error
   * - processing -> processing (需要轮询)
   * - pending -> processing (需要轮询)
   * - completed + indexed = true -> indexed (完成)
   * - completed + indexed = false -> processing (需要轮询)
   */
  private mapApiStatusToFrontendStatus(
    apiStatus: 'pending' | 'processing' | 'completed' | 'error',
    indexed: boolean
  ): 'uploaded' | 'processing' | 'indexed' | 'error' {
    if (apiStatus === 'error') {
      return 'error';
    }

    if (apiStatus === 'processing') {
      return 'processing';
    }

    // pending 状态也需要轮询
    if (apiStatus === 'pending') {
      return 'processing';
    }

    if (apiStatus === 'completed' && indexed) {
      return 'indexed';
    }

    // completed 但未索引，仍需轮询
    if (apiStatus === 'completed') {
      return 'processing';
    }

    // 默认返回 processing 确保会轮询
    return 'processing';
  }

  /**
   * Converts API date string to frontend date format
   * API format: "2025-12-24T16:01:11"
   * Frontend format: "2024/11/25" (YYYY/MM/DD)
   */
  private formatUploadDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}/${month}/${day}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  }

  /**
   * Maps a single API response file item to frontend KnowledgeBaseFile model
   */
  private fromSingle(apiResponse: KnowledgeBaseFileApiResponse): KnowledgeBaseFile {
    // Determine file type: try API file_type first, then infer from file name
    let fileType = this.mapApiFileTypeToFrontendType(apiResponse.file_type);
    if (!fileType) {
      fileType = this.inferFileTypeFromFileName(apiResponse.original_name);
    }

    return {
      id: apiResponse.id,
      file_id: apiResponse.id, // 使用 API 返回的 id 作为 file_id 用于轮询
      name: apiResponse.original_name,
      type: 'file',
      upload_date: this.formatUploadDate(apiResponse.added_at),
      status: this.mapApiStatusToFrontendStatus(apiResponse.processing_status, apiResponse.indexed),
      size: apiResponse.size_bytes,
      file_type: fileType,
      // Map article metadata fields
      title: apiResponse.title,
      authors: apiResponse.authors,
      journal: apiResponse.journal,
      year: apiResponse.year,
      abstract: apiResponse.abstract,
      keywords: apiResponse.keywords,
    };
  }

  /**
   * Maps API response to frontend paginated response
   * Handles multiple formats: single item, array, or paginated response
   */
  from(
    apiResponse: unknown,
    options?: { page?: number; page_size?: number }
  ): PaginatedKnowledgeBaseFilesResponse {
    // Handle paginated response format
    if (
      apiResponse &&
      typeof apiResponse === 'object' &&
      'items' in apiResponse &&
      Array.isArray((apiResponse as unknown as KnowledgeBaseFilesPaginatedApiResponse).items)
    ) {
      const paginatedResponse = apiResponse as unknown as KnowledgeBaseFilesPaginatedApiResponse;
      return {
        items: paginatedResponse.items.map(item => this.fromSingle(item)),
        total: paginatedResponse.total,
        page: paginatedResponse.page,
        per_page: paginatedResponse.page_size,
        pages: paginatedResponse.pages,
      };
    }

    // Handle array format
    if (Array.isArray(apiResponse)) {
      const items = apiResponse as KnowledgeBaseFileApiResponse[];
      return {
        items: items.map(item => this.fromSingle(item)),
        total: items.length,
        page: options?.page || 1,
        per_page: options?.page_size || 20,
        pages: Math.ceil(items.length / (options?.page_size || 20)),
      };
    }

    // Handle single item format - wrap in paginated response
    if (
      apiResponse &&
      typeof apiResponse === 'object' &&
      'id' in apiResponse &&
      'original_name' in apiResponse
    ) {
      const singleItem = this.fromSingle(apiResponse as unknown as KnowledgeBaseFileApiResponse);
      return {
        items: [singleItem],
        total: 1,
        page: options?.page || 1,
        per_page: options?.page_size || 20,
        pages: 1,
      };
    }

    // Fallback: return empty paginated response
    return {
      items: [],
      total: 0,
      page: options?.page || 1,
      per_page: options?.page_size || 20,
      pages: 0,
    };
  }
}

// Export a singleton instance for convenience
export const knowledgeBaseFileMapper = new KnowledgeBaseFileMapper();

export interface CreateKnowledgeBaseData {
  name: string;
  description?: string;
  is_public?: boolean;
  iconType?: KnowledgeBaseIconType; // Icon or logo selection (exclusive OR)
}

export interface PaginatedKnowledgeBasesResponse {
  items: KnowledgeBase[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

class KnowledgeBaseService {
  async getKnowledgeBases(options?: {
    is_public?: boolean;
    is_active?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<KnowledgeBase[]> {
    try {
      console.log(
        'Fetching knowledge bases from:',
        `${process.env.REACT_APP_API_URL}/api/v1/knowledge-bases`,
        'with options:',
        options
      );

      const response = await http.get('/api/v1/knowledge-bases', {
        params: options,
        // Add timeout configuration
        timeout: 10000, // 10 seconds timeout
      });

      console.log('Knowledge bases API response status:', response.status);
      console.log('Response data:', response.data);

      // Handle different API response formats
      let data = response.data;

      // If the response is paginated with 'items' array (based on the provided response format)
      if (data && typeof data === 'object' && 'items' in data && Array.isArray(data.items)) {
        return data.items;
      }

      // If the response is wrapped in a data object, extract the array
      if (data && typeof data === 'object' && 'data' in data) {
        data = data.data;
      }

      // If the response is wrapped in a results object, extract the array
      if (data && typeof data === 'object' && 'results' in data) {
        data = data.results;
      }

      // Ensure we return an array
      const result = Array.isArray(data) ? data : [];
      console.log('Final knowledge bases array:', result);
      return result;
    } catch (error: any) {
      console.error('Failed to fetch knowledge bases:', error);

      // Log detailed error information
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
        console.error('Error response headers:', error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('Error request:', error.request);
        console.error('Network error - no response received');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error message:', error.message);
      }

      // Check if it's an insufficient resources error
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        console.error('Request timed out - possible insufficient resources');
      }

      // Return mock data for development when API is down
      console.log('Returning mock knowledge bases data due to API error');
      return [
        {
          id: 'mock-1',
          name: 'Machine Learning Fundamentals',
          description: 'Comprehensive guide to machine learning concepts and algorithms',
          is_active: true,
          is_public: false,
          is_default: false,
          owner_id: 1,
          owner: { id: 1, email: 'user@example.com', full_name: 'Demo User' },
          chunk_size: 1000,
          chunk_overlap: 200,
          document_count: 15,
          total_tokens: 50000,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        {
          id: 'mock-2',
          name: 'Research Papers 2024',
          description: 'Collection of latest research papers on AI and ML',
          is_active: true,
          is_public: true,
          is_default: true,
          owner_id: 1,
          owner: { id: 1, email: 'user@example.com', full_name: 'Demo User' },
          chunk_size: 1000,
          chunk_overlap: 200,
          document_count: 42,
          total_tokens: 120000,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
      ];
    }
  }

  async getKnowledgeBasesPaginated(options?: {
    includes?: {
      is_owner?: boolean;
      is_public?: boolean;
      is_shared?: boolean;
    };
    page?: {
      page?: number;
      page_size?: number;
    };
  }): Promise<PaginatedKnowledgeBasesResponse> {
    try {
      const response = await http.get('/api/v1/knowledge-bases', { params: options });
      const data = response.data;

      // Return the full paginated response if it has the expected format
      if (data && typeof data === 'object' && 'items' in data && Array.isArray(data.items)) {
        return data as PaginatedKnowledgeBasesResponse;
      }

      // Fallback: convert array responses to paginated format
      const items = Array.isArray(data) ? data : [];
      return {
        items,
        total: items.length,
        page: options?.page?.page || 1,
        per_page: options?.page?.page_size || 20,
        pages: 1,
      };
    } catch (error) {
      console.error('Failed to fetch paginated knowledge bases:', error);
      return {
        items: [],
        total: 0,
        page: 1,
        per_page: 20,
        pages: 0,
      };
    }
  }

  async getKnowledgeBase(id: string): Promise<KnowledgeBase | null> {
    try {
      console.log(`Fetching knowledge base with ID: ${id}`);
      const response = await http.get(`/api/v1/knowledge-bases/${id}`);
      console.log('Knowledge base API response:', response.data);
      return response.data as KnowledgeBase;
    } catch (error) {
      console.error(`Failed to fetch knowledge base ${id}:`, error);
      return null;
    }
  }

  async createKnowledgeBase(data: CreateKnowledgeBaseData): Promise<KnowledgeBase | null> {
    try {
      const { iconType, ...restData } = data;

      const payload = {
        ...restData,
        meta_data: iconType ? iconTypeToMetaData(iconType) : undefined,
      };

      const response = await http.post('/api/v1/knowledge-bases', payload);
      return response.data as KnowledgeBase;
    } catch (error) {
      console.error('Failed to create knowledge base:', error);
      return null;
    }
  }

  async updateKnowledgeBase(
    id: string,
    data: Partial<CreateKnowledgeBaseData>
  ): Promise<KnowledgeBase | null> {
    try {
      const { iconType, ...restData } = data;

      const payload = {
        ...restData,
        meta_data: iconType ? iconTypeToMetaData(iconType) : undefined,
      };

      const response = await http.put(`/api/v1/knowledge-bases/${id}`, payload);
      return response.data as KnowledgeBase;
    } catch (error) {
      console.error(`Failed to update knowledge base ${id}:`, error);
      return null;
    }
  }

  async deleteKnowledgeBase(id: string): Promise<boolean> {
    try {
      await http.delete(`/api/v1/knowledge-bases/${id}`);
      return true;
    } catch (error) {
      console.error(`Failed to delete knowledge base ${id}:`, error);
      return false;
    }
  }

  async activateKnowledgeBase(id: string): Promise<KnowledgeBase | null> {
    try {
      const response = await http.post(`/api/v1/knowledge-bases/${id}/activate`);
      return response.data as KnowledgeBase;
    } catch (error) {
      console.error(`Failed to activate knowledge base ${id}:`, error);
      return null;
    }
  }

  async deactivateKnowledgeBase(id: string): Promise<KnowledgeBase | null> {
    try {
      const response = await http.post(`/api/v1/knowledge-bases/${id}/deactivate`);
      return response.data as KnowledgeBase;
    } catch (error) {
      console.error(`Failed to deactivate knowledge base ${id}:`, error);
      return null;
    }
  }

  async getKnowledgeBaseFiles(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/v1/knowledge-bases/${id}/files`);
      return response.data as any[];
    } catch (error) {
      console.error(`Failed to fetch files for knowledge base ${id}:`, error);
      return [];
    }
  }

  // Get files with pagination and filtering
  async getKnowledgeBaseFilesPaginated(
    id: string,
    params?: {
      page?: number;
      page_size?: number;
      search?: string;
      status?: string;
      sort_by?: string;
      sort_order?: 'asc' | 'desc';
    }
  ): Promise<PaginatedKnowledgeBaseFilesResponse> {
    try {
      const response = await http.get(`/api/v1/knowledge-bases/${id}/files`, { params });
      return knowledgeBaseFileMapper.from(response.data, {
        page: params?.page,
        page_size: params?.page_size,
      });
    } catch (error) {
      console.error(`Failed to fetch paginated files for knowledge base ${id}:`, error);
      return {
        items: [],
        total: 0,
        page: params?.page || 1,
        per_page: params?.page_size || 20,
        pages: 0,
      };
    }
  }

  async getKnowledgeBaseShares(id: string): Promise<KnowledgeBaseShare[]> {
    try {
      const response = await http.get(`/api/v1/knowledge-bases/${id}/shares`);
      return response.data as KnowledgeBaseShare[];
    } catch (error) {
      console.error(`Failed to fetch shares for knowledge base ${id}:`, error);
      return [];
    }
  }

  async getDefaultKnowledgeBase(): Promise<KnowledgeBase | null> {
    try {
      const response = await http.get('/api/v1/knowledge-bases/default');
      return response.data as KnowledgeBase;
    } catch (error) {
      console.error('Failed to fetch default knowledge base:', error);
      return null;
    }
  }

  async activateDefaultKnowledgeBase(): Promise<boolean> {
    try {
      await http.post('/api/v1/knowledge-bases/default/activate');
      return true;
    } catch (error) {
      console.error('Failed to activate default knowledge base:', error);
      return false;
    }
  }

  async deactivateDefaultKnowledgeBase(): Promise<boolean> {
    try {
      await http.post('/api/v1/knowledge-bases/default/deactivate');
      return true;
    } catch (error) {
      console.error('Failed to deactivate default knowledge base:', error);
      return false;
    }
  }
}

export const knowledgeBaseService = new KnowledgeBaseService();
export default knowledgeBaseService;
